import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { userlogin } from '../redux/authSlice';
import { getLanguages } from '../redux/translationSlice';
import { PATIENT_WEB_URL } from '../utils/patientWebUrl';
import { policyFor } from '../utils/loginPolicy';
import { isTenantHost, getTypedTenantSubdomain, setTypedTenantSubdomain, getTypedTenantId, setTypedTenantId, TENANT_ID_RE } from '../utils/tenantHost';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useAxiosAuth from '../hooks/useAxiosAuth';
import baseURL from '../utils/baseURL';
import { isSessionMismatch } from '../utils/sessionGuard';
import { useTranslation } from 'react-i18next';
import SupportContactButton from '../components/support/SupportContactButton';
import ModalShell, { ModalFooter } from '../components/common/ModalShell';
import logo from '../assets/img/logo-TT2.png';
import { DollarSign, Lock, User, Eye, EyeOff, Heart, Stethoscope, Languages, ArrowLeft, Building2, Smartphone, ExternalLink, ChevronRight } from 'lucide-react';
import { initializeFCMEarly } from '../utils/initializeFCM';
import useDoctorRegistrationOpen from '../hooks/useDoctorRegistrationOpen';
export default function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const languagePopupRef = useRef(null);
  // A MOC PATIENT IDENTITY TYPED INTO THE PROVIDER DOOR. The backend answers
  // 403 USE_MOC_PORTAL when the e-mail/mobile belongs to an active Mitra One
  // Care patient account with no staff/patient row in this hospital. A toast
  // would vanish before the person could act on it, so this one stays on the
  // screen with the way out attached.
  const [mocPortal, setMocPortal] = useState(false);
  const { i18n, t } = useTranslation('login');
  const axiosInstance = useAxiosAuth();
  const { user, isLoading } = useSelector((state) => state.auth);
  const languages = useSelector((state) => state.translation?.languages || []);
  // Doctor sign-up is open when some organisation accepts it (or the host's
  // does) — hooks/useDoctorRegistrationOpen, not the old platform flag.
  const { open: doctorRegEnabled } = useDoctorRegistrationOpen();
  // Patients never register on this app (owner, 1 Oct 2026): the register
  // link is the doctor's door alone.
  const anyRegistrationEnabled = doctorRegEnabled;

  useEffect(() => {
    if (!languages || languages.length === 0) {
      dispatch(getLanguages({ page: 1, limit: 100, search: '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [isLanguagePopupOpen, setIsLanguagePopupOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '' });
  // Which organisation you're signing in to. Accounts are per-hospital, so
  // the backend resolves a login by (organisation, identifier). `tenants`
  // holds at most the ONE hospital this page is for; the policy below shapes
  // the identifier field for it.
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  // Which credential identifies a user is the SELECTED HOSPITAL's policy now
  // ('email' | 'mobile' | 'both'), carried per tenant by /tenants/public and
  // set by the Super Admin from Tenants → Features. Falls back to email-mode
  // while nothing is known.
  const loginIdentifierMode = policyFor(tenants, selectedTenantId).mode;
  // The modes where BOTH credentials open the door. They differ in what
  // registration demands — 'both' wants two, 'either' wants one — which is a
  // question this screen never asks.
  const BOTH_ACCEPTED = ['both', 'either'];

  // SUBDOMAIN-LOCKED LOGIN (decisions log #4). On <tenant>.mitraonecare.com
  // the hostname already says which hospital this is — it shows as a locked
  // banner and there is nothing to choose.
  const [hostTenant, setHostTenant] = useState(null);

  // SUBDOMAIN ON THE SHARED HOST (infra team, 24 Sep 2026; owner, 25 Sep).
  // app.mitraonecare.com is ONE login page for every customer whose own
  // subdomain is not live yet ("temp mode" — infra adds the DNS record only
  // once the customer commits), and a dropdown of every hospital on the
  // platform is the customer list handed to anyone who opens the page — the
  // infra team ruled it out. So the person TYPES their subdomain ('apollo':
  // the Super Admin sets it the day the tenant is created, DNS or not). It is
  // remembered per browser (utils/tenantHost.js), rides on every request as
  // X-Tenant-Subdomain, and /tenants/public — signed-out — answers with that
  // one hospital, name included. A Tenant ID (TN00012) typed instead is looked
  // up by ?tenantId= (policy, no name) and sent explicitly with the login.
  // Left empty, the backend finds the account by its credentials and, when
  // the same email holds several, answers 409 with just those (handleLogin).
  //
  // A hospital already moved to SUBDOMAIN MODE (hostMode) does not sign in
  // here at all: the page shows its address (appUrl) instead of the password
  // box, and the backend refuses anyway (TENANT_HOST_ONLY).
  const [orgInput, setOrgInput] = useState(() => getTypedTenantId() || getTypedTenantSubdomain() || '');
  const [orgStatus, setOrgStatus] = useState('idle'); // idle | checking | ok | unknown
  const orgTimer = useRef(null);

  // ONE ACCOUNT, SEVERAL HOSPITALS (owner, 29 Sep 2026). The same e-mail can
  // hold an account at more than one hospital; after verifying the password
  // the backend answers 409 TENANT_SELECTION_REQUIRED with exactly those
  // hospitals. That is a question, not an error: the page asks it in a modal
  // ("Choose your organisation") and re-submits the SAME credentials with the
  // hospital picked. Nothing is pre-selected and nothing is chosen for the
  // person — the page used to sign them into tenants[0] silently. The list
  // lives in this state only (never in local/sessionStorage) and is dropped
  // the moment the modal closes. Same flow as the Pro-App's Login screen.
  const [tenantChoices, setTenantChoices] = useState(null);
  const choiceDialogRef = useRef(null);
  const submitButtonRef = useRef(null);

  const resolveOrganisation = async (raw) => {
    const typed = String(raw || '').trim();
    const asId = TENANT_ID_RE.test(typed.toUpperCase()) ? typed.toUpperCase() : null;
    const id = setTypedTenantId(asId);
    const sub = asId ? setTypedTenantSubdomain('') : setTypedTenantSubdomain(typed);
    const value = id || sub || '';
    if (!value) {
      setTenants([]);
      setSelectedTenantId('');
      setOrgStatus(typed ? 'unknown' : 'idle');
      return;
    }
    setOrgStatus('checking');
    try {
      // WHICH hospital was typed is answered by /auth/tenant-host (the
      // X-Tenant-Subdomain header names it; null = no such hospital) — never
      // by "the first entry of /tenants/public": a stale token left in this
      // browser makes that endpoint answer with EVERY hospital, and the first
      // one is not the one typed (25 Sep 2026). /tenants/public then only
      // supplies the login policy, matched by id.
      let host = null;
      if (sub) {
        const h = await axiosInstance.get(`${baseURL}/auth/tenant-host`);
        host = h.data?.tenant || null;
      }
      const wantId = id || host?.tenantId || null;
      const res = wantId
        ? await axiosInstance.get(`${baseURL}/tenants/public`, { params: { tenantId: wantId } })
        : { data: { tenants: [] } };
      // A slower answer for an earlier value must not overwrite a newer one.
      if ((getTypedTenantId() || getTypedTenantSubdomain()) !== value) return;
      const found = (res.data?.tenants || []).find((tn) => tn.tenantId === wantId) || null;
      const merged = found ? { ...(host || {}), ...found, name: found.name || host?.name || null } : null;
      if (!merged) setTypedTenantSubdomain('');
      setTenants(merged ? [merged] : []);
      setSelectedTenantId(merged ? merged.tenantId : '');
      setOrgStatus(merged ? 'ok' : 'unknown');
    } catch {
      // Unknown (404 from /auth/tenant-host) or unreachable: forget the typed
      // value so the refused header does not ride on the login request; the
      // text stays in the box for the person to correct.
      setTypedTenantSubdomain('');
      setTenants([]);
      setSelectedTenantId('');
      setOrgStatus('unknown');
    }
  };
  // The resolved hospital lives on its own host (subdomain mode) and this
  // page is not it: send the person there rather than take a password.
  const movedTo = (() => {
    const tn = tenants[0];
    if (!tn || tn.hostMode !== 'subdomain' || !tn.appUrl) return null;
    try { return new URL(tn.appUrl).host === window.location.host ? null : tn.appUrl; } catch { return null; }
  })();

  const onOrgChange = (e) => {
    const next = e.target.value;
    setOrgInput(next);
    setOrgStatus('idle');
    clearTimeout(orgTimer.current);
    orgTimer.current = setTimeout(() => resolveOrganisation(next), 500);
  };
  const onOrgBlur = () => {
    clearTimeout(orgTimer.current);
    resolveOrganisation(orgInput);
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      if (isTenantHost()) {
        // /tenants/public answers with this host's hospital alone (the
        // X-Tenant-Subdomain header rides on the request via useAxiosAuth);
        // /auth/tenant-host names it for the banner.
        try {
          const res = await axiosInstance.get(`${baseURL}/tenants/public`);
          if (!alive) return;
          const list = res.data?.tenants || [];
          setTenants(list);
          if (list.length) setSelectedTenantId(list[0].tenantId);
        } catch {
          if (alive) setTenants([]);
        }
        try {
          const host = await axiosInstance.get(`${baseURL}/auth/tenant-host`);
          if (alive && host.data?.tenant?.tenantId) {
            setHostTenant(host.data.tenant);
            setSelectedTenantId(host.data.tenant.tenantId);
          }
        } catch { /* non-fatal — the backend still resolves by credentials */ }
      } else if (getTypedTenantId() || getTypedTenantSubdomain()) {
        // Remembered from an earlier visit — confirm it still exists.
        resolveOrganisation(getTypedTenantId() || getTypedTenantSubdomain());
      }
    })();
    return () => { alive = false; clearTimeout(orgTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [errors, setErrors] = useState({ email: '', password: '' });

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  // Validate email format
  const validateEmail = (email) => {
    if (!email) {
      return t('login.email_required');
    }
    
    // Sanitize input - remove dangerous characters
    const sanitized = email.replace(/[<>]/g, '');
    
    // Check for whitespace-only
    if (sanitized.trim().length === 0 && sanitized.length > 0) {
      return t('login.email_required');
    }
    
    // Check max length (254 characters is standard email max length)
    if (sanitized.length > 254) {
      return t('login.email_too_long') || 'Email cannot exceed 254 characters';
    }
    
    // Check local part (before @) max 64 characters
    const localPart = sanitized.split('@')[0];
    if (localPart && localPart.length > 64) {
      return t('login.email_local_part_too_long');
    }
    
    // Check domain part (after @) max 253 characters
    const domainPart = sanitized.split('@')[1];
    if (domainPart && domainPart.length > 253) {
      return t('login.email_domain_part_too_long');
    }
    
    // Check email format
    if (!emailRegex.test(sanitized)) {
      return t('login.invalid_email_format');
    }

    return '';
  };

  // Validate a mobile number: 6–15 digits once formatting is stripped (covers
  // every national numbering plan; numbers are stored without country code).
  const validateMobile = (value) => {
    const digits = value.replace(/[^0-9]/g, '');
    if (!digits) {
      return t('login.mobile_required', 'Mobile number is required');
    }
    if (digits.length < 6 || digits.length > 15) {
      return t('login.invalid_mobile_format', 'Enter a valid mobile number (6–15 digits)');
    }
    return '';
  };

  // Mode-aware identifier validation. An '@' anywhere means the person is
  // typing an email — accepted even in 'mobile' mode (the backend only honours
  // it for the super admin; hinting at that here would advertise the
  // break-glass, so the field stays phone-worded).
  const validateIdentifier = (value) => {
    if (!value) {
      if (loginIdentifierMode === 'mobile') return t('login.mobile_required', 'Mobile number is required');
      // 'both' and 'either' both accept either credential at the door — they
      // differ only in what REGISTRATION demands, which is not this screen.
      if (BOTH_ACCEPTED.includes(loginIdentifierMode)) return t('login.identifier_required', 'Email or mobile number is required');
      return t('login.email_required');
    }
    if (loginIdentifierMode === 'email' || value.includes('@')) return validateEmail(value);
    return validateMobile(value);
  };

  // Validate password
  const validatePassword = (password) => {
    if (!password) {
      return t('login.password_required');
    }
    
    // NEVER sanitize a password (P34). It is hashed on the server and never
    // rendered, so no character in it is dangerous — and stripping `<` `>`
    // here meant a password set on the phone with one of them could never
    // sign in on the web. Validate exactly what was typed.

    // Check for whitespace-only
    if (password.trim().length === 0) {
      return t('login.password_required');
    }

    // Check max length (reasonable limit for password)
    if (password.length > 100) {
      return t('login.password_too_long');
    }
    
    return '';
  };

  // Initialize FCM early (before login)
  useEffect(() => {
    // Initialize FCM as soon as Login page loads
    initializeFCMEarly();
  }, []);

  // Redirect to dashboard when this tab can use an existing session.
  //
  // Two cases:
  //   1. This tab just logged in (Redux `user` got populated) — go to the
  //      role's dashboard.
  //   2. A fresh tab opened /login while another tab in the same browser is
  //      already signed in. Redux `user` is null in this tab, but localStorage
  //      shows an active session — pick the role from there.
  //
  // Skip both cases when the tab is exiled (the cross-tab guard kicked us out
  // because another tab signed in as a different user). Otherwise this effect
  // would ping-pong with PrivateRoute: redirect → mismatch → back to /login.
  useEffect(() => {
    if (isSessionMismatch()) return;

    const role = user?.role || localStorage.getItem('Role');
    const isAuthed = localStorage.getItem('isAuthenticated') === 'true';
    const id = localStorage.getItem('ID');
    if (!isAuthed || !role || !id) return;

    // Super Admin moved to the dedicated admin portal — the notice page
    // explains and signs this session out of the tenant app.
    if (localStorage.getItem('isSuperAdmin') === 'true') {
      navigate('/superadmin-moved', { replace: true });
      return;
    }

    // WHERE THE PERSON WAS HEADED. A public pricing card sends them here with
    // state.next ('/mySubscription' + the packageId it was clicked on), so the
    // plan they picked is still the plan they land on. Only ever a path inside
    // this SPA: anything that is not a single-slash-prefixed path is dropped,
    // because a `next` that can start with '//' or a scheme is an open
    // redirect even when it arrives through router state.
    const next = location.state?.next;
    const safeNext = typeof next === 'string' && /^\/(?!\/)/.test(next) ? next : null;
    if (safeNext) {
      navigate(safeNext, { replace: true, state: location.state?.packageId ? { packageId: location.state.packageId } : undefined });
      return;
    }

    // Staff ('user') had no entry and were sent to '/', which is the PUBLIC
    // home: a signed-in receptionist landed on the marketing page and its
    // "Sign in" sent them round again (30 Sep 2026).
    const roleRoutes = {
      doctor: '/doctorDashboard',
      admin: '/adminDashboard',
      user: '/staffDashboard',
      patient: '/patientDashboard',
    };
    navigate(roleRoutes[role] || '/', { replace: true });
  }, [user, navigate, location.state]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (languagePopupRef.current && !languagePopupRef.current.contains(event.target)) {
        setIsLanguagePopupOpen(false);
      }
    };
    if (isLanguagePopupOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isLanguagePopupOpen]);

  // Handle input changes with validation
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // The password is kept EXACTLY as typed — never stripped, trimmed or
    // truncated (P34): it is hashed, never rendered, and any silent change
    // makes a correct password fail. Too long is reported by validatePassword.
    let finalValue = value;
    if (name === 'email') {
      // Sanitize the identifier only - remove dangerous characters
      finalValue = value.replace(/[<>]/g, '');
      // Enforce max length for email
      if (finalValue.length > 254) {
        finalValue = finalValue.substring(0, 254);
      }
      // Identifiers (email or mobile) are stored and compared lowercase.
      finalValue = finalValue.toLowerCase();
    }
    
    setFormData((prev) => ({ ...prev, [name]: finalValue }));

    // The MOC-portal panel is about the identifier that was typed, so a new
    // one takes it away.
    if (name === 'email' && mocPortal) setMocPortal(false);

    // Validate the identifier (email or mobile, per the platform mode)
    if (name === 'email') {
      const error = validateIdentifier(finalValue);
      setErrors((prev) => ({ ...prev, email: error }));
    }

    // Validate password
    if (name === 'password') {
      const error = validatePassword(finalValue);
      setErrors((prev) => ({ ...prev, password: error }));
    }
  };

  const handleLanguageChange = (language) => {
    i18n.changeLanguage(language);
    setIsLanguagePopupOpen(false);
  };

  // Handle login submission
  const handleLogin = async (e) => {
    e.preventDefault();

    // Final validation before submission
    const emailError = validateIdentifier(formData.email);
    const passwordError = validatePassword(formData.password);
    
    if (emailError || passwordError) {
      setErrors({ email: emailError, password: passwordError });
      toast.warning(t('login.form_errors') || 'Please fix the errors in the form', { autoClose: 2000 });
      return;
    }
    
    if (!formData.email || !formData.password) {
      toast.warning(t('login.fill_all_fields'), { autoClose: 2000 });
      return;
    }

    // A typed organisation the platform does not know: the answer would be a
    // misleading "invalid credentials", so say what is actually wrong.
    if (!hostTenant && orgStatus === 'unknown') {
      toast.error(t('loginpage.organisation_unknown', "We don't know this subdomain. Check it with your organisation."));
      return;
    }
    if (movedTo) {
      toast.info(`${t('loginpage.moved_to', 'This organisation signs in at')} ${movedTo}`);
      return;
    }

    await submitLogin(selectedTenantId || null);
  };

  // One sign-in attempt with the credentials in the form. `tenantId` is the
  // typed/host organisation on a normal submit, or the hospital chosen in the
  // 409 picker on the re-submit.
  const submitLogin = async (tenantId) => {
    try {
      // `identifier` carries email or mobile number; the backend classifies it
      // and enforces the platform's login-identifier mode server-side.
      await dispatch(userlogin({
        formdata: {
          identifier: formData.email.trim(),
          password: formData.password,
          tenantId: tenantId || null,
        },
        axiosInstance,
      })).unwrap();
    } catch (err) {
      // The hospital has moved to its own host — the backend refused this
      // page (api/middleware/tenantHost.js hostOnlyRefusal); show the way.
      // Only the organisation box's own hospital gets the moved notice.
      if (err && err.code === 'TENANT_HOST_ONLY') {
        if (err.appUrl) setTenants((prev) => (prev[0] && prev[0].tenantId === tenantId ? [{ ...prev[0], hostMode: 'subdomain', appUrl: err.appUrl }] : prev));
        toast.info(`${t('loginpage.moved_to', 'This organisation signs in at')} ${err.appUrl || ''}`);
        return;
      }
      // A pending account must prove its contacts + choose a password before
      // it can sign in — route it into the activation flow. Every role takes
      // this door: bulk-uploaded / desk-created patients, and since "Concept 2"
      // (1 Oct 2026) a doctor, staff member OR ADMIN created by someone else,
      // at their first sign-in. Nothing here branches on the role.
      if (err && err.verificationRequired) {
        // tenantId names WHICH organisation's account is being activated.
        // `verify` is the server's exact list of codes it wants, and
        // `activationToken` must travel back on /auth/activate (the staff side
        // is refused without it). Both are absent for patients / old servers —
        // the activation page then falls back to the local policy rule.
        navigate('/activate', {
          state: {
            email: err.email || null,
            phone: err.phone || null,
            tenantId: err.tenantId || tenantId || null,
            role: err.role || null,
            activationToken: err.activationToken || null,
            verify: err.verify || null,
          },
        });
        return;
      }
      // Accounts are per-hospital now: the same email can hold several, and
      // the backend answers 409 with the hospitals this password actually
      // owns (only ever after verifying the password). Ask the person which
      // one (the modal below) — no toast, it is a question, not an error.
      // This also covers "typed the wrong organisation", which used to be a
      // dead-end WRONG_TENANT error.
      if (err && err.code === 'TENANT_SELECTION_REQUIRED' && Array.isArray(err.tenants) && err.tenants.length) {
        setTenantChoices(err.tenants);
        return;
      }
      // The same 409 with NOTHING to choose from (the one account sits at a
      // hospital that is not active, or the organisation typed is another
      // one): there is no picker to show, and saying nothing left Sign In
      // looking dead. It is a refusal — say so.
      if (err && err.code === 'TENANT_SELECTION_REQUIRED') {
        toast.error(t('auth.invalidCredentials', 'Invalid credentials'));
        return;
      }
      // Login-identifier rules: the typed kind of credential is switched off
      // (LOGIN_METHOD_NOT_ALLOWED) or one mobile number + password owns
      // several accounts at one hospital (IDENTIFIER_AMBIGUOUS — sign in with
      // email instead). Backend message arrives already localised.
      if (err && (err.code === 'LOGIN_METHOD_NOT_ALLOWED' || err.code === 'IDENTIFIER_AMBIGUOUS')) {
        toast.warning(err.message, { autoClose: 5000 });
        return;
      }
      // CONNECTED hospitals keep no patient login here — their patients sign
      // in on the separate Mitra One Care Patient App (backend answers 403
      // with directions). Surface that message instead of a generic failure.
      if (err && err.code === 'PATIENT_USE_MOC_APP') {
        toast.info(err.message, { autoClose: 6000 });
        return;
      }
      // A SELF-REGISTERED MOC PATIENT (no hospital account here at all). Not a
      // wrong password and not a missing account — the identity exists, at the
      // patient portal. Shown as a standing panel rather than a toast so the
      // link to that portal survives long enough to be clicked.
      if (err && err.code === 'USE_MOC_PORTAL') {
        setMocPortal(true);
        return;
      }
      // Other errors are handled in useEffect and useAxiosAuth
    }
  };

  // Picker actions. Choosing closes the modal and re-submits the same
  // credentials for that hospital; cancelling signs nobody in. Either way
  // focus goes back to the Sign In button the person came from.
  const closeTenantChoices = () => {
    setTenantChoices(null);
    submitButtonRef.current?.focus();
  };
  const chooseTenant = (tenantId) => {
    closeTenantChoices();
    submitLogin(tenantId);
  };

  // Focus moves INTO the modal when it opens — onto the dialog itself, not a
  // row, so nothing looks pre-selected — and Tab stays inside it.
  // choiceDialogRef holds the shared frame (common/ModalShell); its first
  // child is the role="dialog" element, made focusable here.
  const choiceDialog = () => choiceDialogRef.current?.firstElementChild || null;
  useEffect(() => {
    const dlg = tenantChoices ? (choiceDialogRef.current?.firstElementChild || null) : null;
    if (dlg) {
      dlg.tabIndex = -1;
      dlg.style.outline = 'none';
      dlg.setAttribute('aria-describedby', 'tenant-choice-hint');
      dlg.focus();
    }
  }, [tenantChoices]);
  const onChoiceKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      closeTenantChoices();
      return;
    }
    if (e.key !== 'Tab') return;
    const focusable = Array.from(choiceDialogRef.current?.querySelectorAll('button:not([disabled])') || []);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === choiceDialog())) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <div data-testid="login-page" className="min-h-screen relative w-full flex flex-col items-center justify-center px-2 sm:px-4 md:px-6 lg:px-8 bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B]/90 overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-20 -right-20 sm:-top-40 sm:-right-40 w-40 h-40 sm:w-80 sm:h-80 bg-white/20 rounded-full mix-blend-multiply filter blur-xl opacity-60 animate-blob"></div>
        <div className="absolute -bottom-20 -left-20 sm:-bottom-40 sm:-left-40 w-40 h-40 sm:w-80 sm:h-80 bg-gray-200/30 rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-blob animation-delay-2000"></div>
        <div className="absolute top-20 left-20 sm:top-40 sm:left-40 w-40 h-40 sm:w-80 sm:h-80 bg-white/10 rounded-full mix-blend-multiply filter blur-xl opacity-40 animate-blob animation-delay-4000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 sm:w-96 sm:h-96 bg-white/5 rounded-full mix-blend-multiply filter blur-2xl opacity-30 animate-blob animation-delay-1000"></div>
      </div>

      {/* Back Button */}
      <div className="absolute top-4 left-4 sm:top-6 sm:left-6 z-50">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 p-1.5 text-white focus:outline-none cursor-pointer bg-white/20 backdrop-blur-sm border border-white/30 shadow-lg rounded-2xl hover:bg-white/30 transition-all duration-300"
          aria-label={t('login.back_button')}
        >
          <ArrowLeft className="h-6 w-6" />
        </button>
      </div>

      {/* Top-right corner: help for anyone who cannot sign in (the support
          mailbox, in a popup — doc/5.10.2026.md, Support mailbox plan), then
          the language switcher. */}
      <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-50 flex items-start gap-2">
        <SupportContactButton className="flex items-center gap-2 p-1.5 text-white focus:outline-none cursor-pointer bg-white/20 backdrop-blur-sm border border-white/30 shadow-lg rounded-2xl hover:bg-white/30 transition-all duration-300" />
        <div className="relative">
        <button
          data-testid="language-toggle-button"
          onClick={() => setIsLanguagePopupOpen(!isLanguagePopupOpen)}
          className="flex items-center gap-2 p-1.5 text-white focus:outline-none cursor-pointer bg-white/20 backdrop-blur-sm border border-white/30 shadow-lg rounded-2xl"
          aria-label={t('login.select_language')}
        >
          <Languages className="h-6 w-6" />
        </button>
        {isLanguagePopupOpen && (
          <div
            data-testid="language-popup"
            ref={languagePopupRef}
            className="absolute right-0 top-full mt-2 w-44 max-h-72 overflow-y-auto bg-white rounded-lg shadow-lg p-2 z-40 border border-gray-200"
          >
            {languages && languages.length > 0 ? (
              languages.map((langItem) => {
                const code = (langItem.code || '').toLowerCase();
                const active = i18n.language === code;
                return (
                  <button
                    key={langItem.languageId || code}
                    data-testid={`language-option-${code}`}
                    onClick={() => handleLanguageChange(code)}
                    className={`w-full text-left mb-1 last:mb-0 px-4 cursor-pointer py-2 text-sm rounded-md ${active
                      ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                      }`}
                  >
                    <span className="flex items-center justify-between gap-2">
                      <span className="truncate">{langItem.name}</span>
                      <span className={`text-[10px] font-bold uppercase ${active ? 'text-white/80' : 'text-gray-400'}`}>{code}</span>
                    </span>
                  </button>
                );
              })
            ) : (
              <button
                data-testid="english-option"
                onClick={() => handleLanguageChange('en')}
                className={`w-full text-left px-4 cursor-pointer py-2 text-sm rounded-md ${i18n.language === 'en'
                  ? 'bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] text-white'
                  : 'text-gray-700 hover:bg-gray-100'
                  }`}
              >
                {t('login.language_english')}
              </button>
            )}
          </div>
        )}
        </div>
      </div>


      {/* Custom Styles */}
      <style>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes typewriter {
          from {
            width: 0;
          }
          to {
            width: 100%;
          }
        }
        @keyframes blink {
          0%, 50% {
            border-color: transparent;
          }
          51%, 100% {
            border-color: #4ABDBD;
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .animate-pulse-slow {
          animation: pulse 3s ease-in-out infinite;
        }
        .animate-slideInUp {
          animation: slideInUp 0.8s ease-out;
        }
        .animate-slideInLeft {
          animation: slideInLeft 0.8s ease-out;
        }
        .animate-slideInRight {
          animation: slideInRight 0.8s ease-out;
        }
        .animate-typewriter {
          animation: typewriter 2s steps(40, end);
        }
        .animate-blink {
          animation: blink 1s infinite;
        }
        .text-gradient {
          background: linear-gradient(135deg, #4ABDBD 0%, #3A9B9B 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .glass-effect {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }
      `}</style>

      {/* Main Container */}
      <div className="w-full max-w-7xl flex flex-col lg:flex-row items-center justify-center gap-4 sm:gap-6 md:gap-8 lg:gap-12 relative z-10 py-4 sm:py-8">

        {/* Left Section - Branding */}
        <div className="flex flex-col items-center justify-center gap-4 sm:gap-6 md:gap-8 w-full lg:w-1/2 animate-slideInLeft px-4 sm:px-0">
          {/* Animated Logo */}
          <div className="relative">
            <div className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 lg:w-32 lg:h-32 bg-white rounded-full flex items-center justify-center animate-float shadow-2xl border border-white/30">
              <img src={logo} className="w-20 h-20" />
            </div>
            <div className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 w-4 h-4 sm:w-6 sm:h-6 md:w-8 md:h-8 bg-white/40 rounded-full animate-ping"></div>
            <div className="absolute -bottom-1 -left-1 sm:-bottom-2 sm:-left-2 w-3 h-3 sm:w-4 sm:h-4 md:w-6 md:h-6 bg-white/30 rounded-full animate-ping animation-delay-2000"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24 sm:w-32 sm:h-32 md:w-36 md:h-36 lg:w-40 lg:h-40 bg-white/5 rounded-full animate-pulse-slow"></div>
          </div>

          {/* Animated Title */}
          <div className="text-center px-2">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-2 sm:mb-4 drop-shadow-lg">
              <span className="inline-block animate-slideInUp" style={{ animationDelay: '0.2s' }}>M</span>
              <span className="inline-block animate-slideInUp" style={{ animationDelay: '0.3s' }}>i</span>
              <span className="inline-block animate-slideInUp" style={{ animationDelay: '0.4s' }}>t</span>
              <span className="inline-block animate-slideInUp" style={{ animationDelay: '0.5s' }}>r</span>
              <span className="inline-block animate-slideInUp" style={{ animationDelay: '0.6s' }}>a</span>
              <span className="inline-block animate-slideInUp" style={{ animationDelay: '0.7s' }}>{' '}</span>
              <span className="inline-block animate-slideInUp" style={{ animationDelay: '0.8s' }}>O</span>
              <span className="inline-block animate-slideInUp" style={{ animationDelay: '0.9s' }}>n</span>
              <span className="inline-block animate-slideInUp" style={{ animationDelay: '1.0s' }}>e</span>
              <span className="inline-block animate-slideInUp" style={{ animationDelay: '1.1s' }}>{' '}</span>
              <span className="inline-block animate-slideInUp" style={{ animationDelay: '1.2s' }}>C</span>
              <span className="inline-block animate-slideInUp" style={{ animationDelay: '1.3s' }}>a</span>
              <span className="inline-block animate-slideInUp" style={{ animationDelay: '1.4s' }}>r</span>
              <span className="inline-block animate-slideInUp" style={{ animationDelay: '1.5s' }}>e</span>
            </h1>
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/90 font-light animate-slideInUp drop-shadow-md" style={{ animationDelay: '1.6s' }}>
              {t('login.welcome_quote1')}
            </p>
          </div>

          {/* Floating Icons */}
          <div className="flex gap-4 sm:gap-6 md:gap-8 animate-slideInUp" style={{ animationDelay: '1.8s' }}>
            <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center animate-float border border-white/30 shadow-lg">
              <Stethoscope className="text-white text-lg sm:text-xl md:text-2xl" />
            </div>
            <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-white/15 backdrop-blur-sm rounded-full flex items-center justify-center animate-float border border-white/20 shadow-lg" style={{ animationDelay: '1s' }}>
              <Heart className="text-white text-lg sm:text-xl md:text-2xl" />
            </div>
            <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center animate-float border border-white/25 shadow-lg" style={{ animationDelay: '2s' }}>
              <Stethoscope className="text-white text-lg sm:text-xl md:text-2xl" />
            </div>
          </div>
        </div>

        {/* Right Section - Login Form */}
        <div data-testid="login-form-container" className="relative z-10 bg-white/95 backdrop-blur-md p-4 sm:p-6 md:p-8 lg:p-12 rounded-2xl sm:rounded-3xl shadow-2xl w-full max-w-sm sm:max-w-md flex flex-col gap-4 sm:gap-6 md:gap-8 animate-slideInRight border border-white/20 mx-2 sm:mx-0">
          {/* Welcome Quote */}
          <p data-testid="welcome-quote" className="text-center text-gray-700 text-sm sm:text-base md:text-lg font-medium italic animate-slideInUp" style={{ animationDelay: '0.5s' }}>
            "{t('login.welcome_quote')}"
          </p>

          {/* Main Content */}
          <div className="flex justify-center">
            <form onSubmit={handleLogin} className="w-full" data-testid="login-form">
              <h2
                data-testid="login-title"
                className="text-lg sm:text-xl md:text-2xl font-bold text-center mb-3 sm:mb-4 md:mb-6 text-[#4ABDBD]"
              >
                {t('login.sign_in_title')}
              </h2>

              {/* 403 USE_MOC_PORTAL — the identity is a Mitra One Care patient
                  account, which this app has no door for. The button only
                  exists when this deployment HAS a patient site
                  (VITE_PATIENT_WEB_URL); otherwise the sentence stands alone,
                  because a button pointing nowhere is worse than none. */}
              {mocPortal && (
                <div
                  data-testid="login-moc-portal"
                  className="mb-3 sm:mb-4 rounded-lg sm:rounded-xl border border-[#4ABDBD] bg-[#E7F5F4] p-3 sm:p-4 text-left"
                >
                  <p className="text-sm font-bold text-[#2D8A8A]">{t('login.moc_portal_title')}</p>
                  <p className="mt-1 text-xs sm:text-sm text-gray-700">{t('login.moc_portal_body')}</p>
                  {PATIENT_WEB_URL && (
                    // A real <a>, not a button: this leaves the SPA for
                    // another deployment, so it must open like any external
                    // link (middle-click, copy address, a browser's own
                    // history).
                    <a
                      href={`${PATIENT_WEB_URL}/#/login`}
                      data-testid="login-moc-portal-cta"
                      className="mt-3 inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] px-3 py-2 text-xs sm:text-sm font-semibold text-white transition-all hover:shadow-lg"
                    >
                      {t('login.moc_portal_cta')} <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </div>
              )}

              {/* Organisation — which hospital you're signing in to. Sits
                  above the credentials because it IS part of them: accounts
                  are per-hospital. On a tenant subdomain it is a locked banner;
                  on the shared host it is typed, never listed (see the note
                  on orgInput above). */}
              {hostTenant && (
                <div
                  data-testid="tenant-host-banner"
                  className="flex items-center gap-2 rounded-lg sm:rounded-xl px-3 py-2.5 mb-3 sm:mb-4 bg-[#E7F5F4] border border-[#4ABDBD] text-[#2D8A8A] text-sm font-semibold"
                >
                  <Building2 className="w-4 h-4 flex-shrink-0" />
                  {t('loginpage.signing_in_to', 'Signing in to')} {hostTenant.name}
                </div>
              )}
              {!hostTenant && !isTenantHost() && (
                <div data-testid="tenant-org-container" className="mb-3 sm:mb-4">
                  <div
                    className={`relative flex items-center border rounded-lg sm:rounded-xl px-2 sm:px-3 md:px-4 py-2 sm:py-3 bg-white/80 backdrop-blur-sm focus-within:ring-2 focus-within:bg-white transition-all duration-300 shadow-sm ${
                      orgStatus === 'unknown'
                        ? 'border-red-300 focus-within:ring-red-400 focus-within:border-red-400'
                        : 'border-gray-200 focus-within:ring-[#4ABDBD] focus-within:border-[#4ABDBD]'
                    }`}
                  >
                    <Building2 className="mr-2 sm:mr-2.5 md:mr-3 text-[#4ABDBD] w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                    <input
                      data-testid="tenant-org-input"
                      name="organisation"
                      type="text"
                      autoComplete="organization"
                      autoCapitalize="none"
                      spellCheck={false}
                      value={orgInput}
                      onChange={onOrgChange}
                      onBlur={onOrgBlur}
                      placeholder={t('loginpage.organisation_placeholder', 'Organisation subdomain (e.g. apollo)')}
                      className="w-full bg-transparent focus:outline-none text-gray-800 text-sm sm:text-sm md:text-base"
                      disabled={isLoading}
                    />
                  </div>
                  {orgStatus === 'ok' && movedTo && (
                    <div data-testid="tenant-org-moved" className="mt-2 rounded-lg border border-[#4ABDBD] bg-[#E7F5F4] p-3 text-left">
                      <p className="text-xs sm:text-sm text-[#2D8A8A] font-semibold">
                        {tenants[0].name ? `${tenants[0].name}: ` : ''}{t('loginpage.moved_to', 'This organisation signs in at')}
                      </p>
                      {/* A real <a>: another host, so it must open like any external link. */}
                      <a
                        href={`${movedTo}/#/login`}
                        data-testid="tenant-org-moved-link"
                        className="mt-1.5 inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] px-3 py-1.5 text-xs sm:text-sm font-semibold text-white transition-all hover:shadow-lg"
                      >
                        {movedTo.replace(/^https?:\/\//, '')} <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    </div>
                  )}
                  {orgStatus === 'ok' && tenants[0] && !movedTo && (
                    <p data-testid="tenant-org-resolved" className="mt-1.5 flex items-center gap-1.5 text-xs sm:text-sm font-medium text-[#2D8A8A]">
                      <Building2 className="w-3.5 h-3.5 flex-shrink-0" />
                      {/* A subdomain answers with the hospital's name; a Tenant ID on
                          purpose does not (see the note above), so confirm it was
                          found — without drawing the id (no system id on screen). */}
                      {tenants[0].name
                        ? `${t('loginpage.signing_in_to', 'Signing in to')} ${tenants[0].name}`
                        : t('loginpage.hospital_found', 'Organisation found')}
                    </p>
                  )}
                  {orgStatus === 'unknown' && (
                    <p data-testid="tenant-org-unknown" className="mt-1.5 text-xs sm:text-sm text-red-500">
                      {t('loginpage.organisation_unknown', "We don't know this subdomain. Check it with your organisation.")}
                    </p>
                  )}
                  {(orgStatus === 'idle' || orgStatus === 'checking') && (
                    <p data-testid="tenant-org-hint" className="mt-1.5 text-xs text-gray-500">
                      {t('loginpage.organisation_hint', 'The subdomain your organisation received from Mitra One Care. Leave it empty if you do not have one.')}
                    </p>
                  )}
                </div>
              )}

              {/* Identifier input — email, mobile number, or either, following
                  the Super Admin's login-identifier mode. The state field stays
                  named `email` so the rest of the form logic is untouched.
                  type="tel" doesn't restrict typing, so an email still fits in
                  mobile mode (super-admin break-glass, deliberately unhinted). */}
              <div
                data-testid="email-input-container"
                className={`relative flex items-center border rounded-lg sm:rounded-xl px-2 sm:px-3 md:px-4 py-2 sm:py-3 mb-3 sm:mb-4 ${errors.email ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-white/80 backdrop-blur-sm'} focus-within:ring-2 focus-within:ring-[#4ABDBD] focus-within:bg-white focus-within:border-[#4ABDBD] transition-all duration-300 shadow-sm`}
              >
                {loginIdentifierMode === 'mobile' ? (
                  <Smartphone
                    data-testid="email-icon"
                    className="mr-2 sm:mr-2.5 md:mr-3 text-[#4ABDBD] text-sm sm:text-base md:text-lg"
                  />
                ) : (
                  <User
                    data-testid="email-icon"
                    className="mr-2 sm:mr-2.5 md:mr-3 text-[#4ABDBD] text-sm sm:text-base md:text-lg"
                  />
                )}
                <input
                  data-testid="email-input"
                  name="email"
                  type={loginIdentifierMode === 'email' ? 'email' : loginIdentifierMode === 'mobile' ? 'tel' : 'text'}
                  placeholder={
                    loginIdentifierMode === 'mobile'
                      ? t('login.mobile_placeholder', 'Mobile number')
                      : BOTH_ACCEPTED.includes(loginIdentifierMode)
                        ? t('login.identifier_placeholder', 'Email or mobile number')
                        : t('login.email_placeholder')
                  }
                  value={formData.email}
                  onChange={handleInputChange}
                  maxLength={254}
                  className={`w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-500 text-sm sm:text-sm md:text-base`}
                  required
                  autoComplete={
                    loginIdentifierMode === 'mobile' ? 'tel' : BOTH_ACCEPTED.includes(loginIdentifierMode) ? 'username' : 'email'
                  }
                />
              </div>
              {errors.email && (
                <p data-testid="email-error" className="text-red-300 text-xs mb-3 -mt-1">
                  {errors.email}
                </p>
              )}

              {/* Password input */}
              <div
                data-testid="password-input-container"
                className={`relative flex items-center border rounded-lg sm:rounded-xl px-2 sm:px-3 md:px-4 py-2 sm:py-3 mb-3 sm:mb-4 ${errors.password ? 'border-red-500 bg-red-50' : 'border-gray-200 bg-white/80 backdrop-blur-sm'} focus-within:ring-2 focus-within:ring-[#4ABDBD] focus-within:bg-white focus-within:border-[#4ABDBD] transition-all duration-300 shadow-sm`}
              >
                <Lock
                  data-testid="password-icon"
                  className="mr-2 sm:mr-2.5 md:mr-3 text-[#4ABDBD] text-sm sm:text-base md:text-lg"
                />
                <input
                  data-testid="password-input"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('login.password_placeholder')}
                  value={formData.password}
                  onChange={handleInputChange}
                  maxLength={100}
                  className={`w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-500 text-sm sm:text-sm md:text-base`}
                  required
                  autoComplete="current-password"
                />
                <button
                  data-testid="toggle-password-visibility"
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="ml-2 sm:ml-2.5 md:ml-3 focus:outline-none hover:scale-105 transition-transform cursor-pointer"
                  aria-label={showPassword ? t('login.hide_password_aria') : t('login.show_password_aria')}
                >
                  {showPassword ? (
                    <Eye data-testid="show-password-icon" className="text-[#4ABDBD] text-sm sm:text-base md:text-lg" />
                  ) : (
                    <EyeOff data-testid="hide-password-icon" className="text-[#4ABDBD] text-sm sm:text-base md:text-lg" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p data-testid="password-error" className="text-red-300 text-xs mb-3 -mt-1">
                  {errors.password}
                </p>
              )}

              {/* Forgot Password Link */}
              <div className="flex justify-end mb-3 sm:mb-4 md:mb-6">
                <p
                  data-testid="forgot-password-link"
                  className="text-right text-xs sm:text-xs text-[#4ABDBD] cursor-pointer hover:text-[#3A9B9B] hover:underline transition-colors"
                  onClick={() => navigate('/forgot-password', { state: { email: formData.email, tenantId: selectedTenantId || null } })}
                >
                  {t('login.forgot_password')}
                </p>
              </div>

              {/* Sign In button */}
              <button
                ref={submitButtonRef}
                data-testid="login-submit-button"
                type="submit"
                disabled={isLoading || errors.email || errors.password}
                className={`w-full py-2.5 sm:py-3 font-bold rounded-lg sm:rounded-xl text-white bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] hover:from-[#4ABDBD]/90 hover:to-[#3A9B9B]/90 transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3 text-sm sm:text-sm md:text-base shadow-lg hover:shadow-xl transform hover:scale-105 ${isLoading || errors.email || errors.password ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'
                  }`}
              >
                {isLoading ? (
                  <span
                    data-testid="login-loading-spinner"
                    className="w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 border-2 border-white border-t-transparent rounded-full animate-spin"
                  ></span>
                ) : (
                  <>
                    <Stethoscope className="text-sm sm:text-base md:text-lg" />
                    {t('login.sign_in_button')}
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Register Link — hidden when admin has disabled all sign-up flows */}
          {anyRegistrationEnabled && (
            <div className="text-center mt-4">
              <p className="text-sm text-gray-600">
                {t('login.no_account')}{' '}
                <span
                  onClick={() => navigate('/register-choice')}
                  className="text-[#4ABDBD] cursor-pointer hover:text-[#3A9B9B] hover:underline font-medium transition-colors"
                >
                  {t('login.register_link')}
                </span>
              </p>
            </div>
          )}

          {/* Footer */}
          <footer data-testid="login-footer" className="text-center text-xs sm:text-sm text-gray-600 animate-slideInUp" style={{ animationDelay: '1.7s' }}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-2">
              <span>
                {t('login.footer_copyright', { year: new Date().getFullYear() })}
              </span>
            </div>
            <span data-testid="powered-by" className="text-[#4ABDBD] font-medium">
              <span data-testid="app-version" className="text-[#4ABDBD] font-medium mr-3">
                {t('login.app_version')}
              </span>
              {t('login.powered_by')}
            </span>
          </footer>
        </div>
      </div>

      {/* 409 TENANT_SELECTION_REQUIRED — "Choose your organisation". Each row
          is a button naming the hospital, with type · city (country when there
          is no city) under it when the server sent them. The tenantId is only
          the value a row submits; it is never drawn (no system id on screen). */}
      {tenantChoices && (
        // A press on the dimmed page cancels, as before the shared frame.
        <div
          ref={choiceDialogRef}
          onKeyDown={onChoiceKeyDown}
          onMouseDown={(e) => { if (e.target === e.currentTarget.firstElementChild) closeTenantChoices(); }}
        >
          <ModalShell
            title={t('login.choose_organisation', 'Choose your organisation')}
            onClose={closeTenantChoices}
            closeLabel={t('common.cancel', 'Cancel')}
            size="md"
            zClass="z-[100]"
            testId="tenant-choice-modal"
            closeTestId="tenant-choice-close"
            footer={(
              <ModalFooter
                onCancel={closeTenantChoices}
                cancelLabel={t('common.cancel', 'Cancel')}
                cancelTestId="tenant-choice-cancel"
              />
            )}
          >
              <p id="tenant-choice-hint" className="text-sm text-slate-500 mb-3">
                {t('login.choose_organisation_hint', 'Your account exists at more than one organisation. Choose the one to sign in to.')}
              </p>
              <ul className="flex flex-col gap-2" data-testid="tenant-choice-list">
                {tenantChoices.map((tn) => {
                  const where = [tn.typeName, tn.city || tn.countryName].filter(Boolean).join(' · ');
                  return (
                    <li key={tn.tenantId}>
                      <button
                        type="button"
                        data-testid="tenant-choice-row"
                        onClick={() => chooseTenant(tn.tenantId)}
                        disabled={isLoading}
                        className="w-full flex items-center gap-3 text-left rounded-xl border border-gray-200 bg-white px-3 py-2.5 hover:border-[#4ABDBD] hover:bg-[#E7F5F4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4ABDBD] transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        <span className="w-10 h-10 rounded-full bg-[#E7F5F4] flex items-center justify-center flex-shrink-0">
                          <Building2 className="w-[18px] h-[18px] text-[#2D8A8A]" />
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="block text-sm font-semibold text-gray-800 truncate">
                            {tn.name || t('sharedVisits.hospital', 'Organisation')}
                          </span>
                          {where && (
                            <span data-testid="tenant-choice-where" className="block text-xs text-gray-500 mt-0.5 truncate">
                              {where}
                            </span>
                          )}
                        </span>
                        <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      </button>
                    </li>
                  );
                })}
              </ul>
          </ModalShell>
        </div>
      )}
    </div>
  );
}